/**
 * Navigation Types
 *
 * Type definitions for centralized module configuration and navigation
 */

import type { NavigationMenuItem } from '@nuxt/ui'
import { SystemModulesData } from '@openbox/shared-types/lib/system/system.enums'

/**
 * System Module IDs from shared-types
 * These match the module IDs (UUIDs) returned by the backend
 *
 * We use the actual UUIDs from SystemModulesData to ensure proper matching
 * with the user's assigned modules from the backend
 */
export const SystemModuleId = {
  Customers: SystemModulesData.Customers.id,
  Donations: SystemModulesData.Donations.id,
  Providers: SystemModulesData.Providers.id,
  Services: SystemModulesData.Services.id,
  Legal: SystemModulesData.Legal.id,
  Invoices: SystemModulesData.Invoices.id,
  Purchases: SystemModulesData.Purchases.id,
  Echarges: SystemModulesData.Echarges.id,
  Receivable: SystemModulesData.Receibable.id, // Note: typo in shared-types
  Accounting: SystemModulesData.Accounting.id,
  Taxes: SystemModulesData.Taxes.id,
  Inventories: SystemModulesData.Inventories.id,
  Reports: SystemModulesData.Reports.id,
  Church: SystemModulesData.Church.id,
  FileCenter: SystemModulesData.FileCenter.id,
} as const

export type SystemModuleId =
  (typeof SystemModuleId)[keyof typeof SystemModuleId]

/**
 * Special Module IDs for modules not managed by backend
 * These modules are always accessible and have special handling
 */
export enum SpecialModuleId {
  Management = '__MANAGEMENT__',
}

/**
 * Combined module IDs (backend + special)
 */
export type ModuleId = SystemModuleId | SpecialModuleId

/**
 * Display condition function type
 * Returns true if the module/item should be displayed
 */
export type DisplayConditionFn = () => boolean

/**
 * Extended NavigationMenuItem with i18n support
 * Adds labelKey for dynamic translation of menu item labels
 */
export interface ModuleNavigationMenuItem extends NavigationMenuItem {
  /** i18n key for item label (e.g., 'modules.customers.newSubmenu') */
  labelKey?: string
  /** Override children type to support labelKey recursively */
  children?: ModuleNavigationMenuItem[]
}

/**
 * Module configuration
 * Defines the structure and behavior of each system module
 */
export interface ModuleConfig {
  /** Unique module identifier (matches backend module ID or special ID) */
  id: ModuleId
  /** Display title for the module (fallback if no i18n key) */
  title: string
  /** i18n key for module title (e.g., 'modules.customers.name') */
  titleKey?: string
  /** Icon name (Lucide icon class) */
  icon: string
  /** Base route path for the module */
  basePath: string
  /** Display order (lower numbers appear first) */
  order: number
  /** Optional function to determine if module should be displayed */
  displayCondition?: DisplayConditionFn
  /** Navigation items/submenus for this module */
  items?: ModuleNavigationMenuItem[]
  /** Whether this module is shown in the footer instead of main nav */
  isFooter?: boolean
  /** Whether this is a special module (not managed by backend permissions) */
  isSpecial?: boolean
}

/**
 * Module configuration map
 * Centralized registry of all system modules
 */
export type ModulesConfigMap = Record<string, ModuleConfig>
